import {
  Action,
  ActionSubject,
  type FeatureEvents,
  type ScreenEvent,
  TrackAction,
  TrackActionSubject,
  type TrackEvent,
  type UIEvent,
} from '../new-analytics-types';

const unifiedTeamProfileScreen = 'unifiedTeamProfileScreen';

type ScreenKeys =
  | 'unifiedTeamProfileScreen'
  | 'addTeamModalScreen'
  | 'editTeamNameModalScreen'
  | 'editTeamDescriptionModalScreen'
  | 'deleteTeamModalScreen'
  | 'removeMemberFromTeamModalScreen'
  | 'teamPendingRequestsTabScreen';

type UiKeys =
  | 'addMembersToTeamButtonClicked'
  | 'dialogOpenedTeamDeletion'
  | 'removeMemberFromTeamButtonClicked'
  | 'addMembersToTeamModalCancelButtonClicked'
  | 'dialogClosedTeamDeletion'
  | 'removeMemberFromTeamModalCancelButtonClicked'
  | 'removeMemberFromTeamConfirmButtonClicked'
  | 'addTeamModalConfirmButtonClicked'
  | 'deleteTeamConfirmButtonClicked'
  | 'editTeamNameButtonClicked'
  | 'editTeamDescriptionButtonClicked'
  | 'editTeamNameModalCancelButtonClicked'
  | 'editTeamDescriptionModalCancelButtonClicked'
  | 'editTeamNameSubmitButtonClicked'
  | 'editTeamDescriptionSubmitButtonClicked'
  | 'acceptJoinRequestButtonClicked'
  | 'declineJoinRequestButtonClicked'
  | 'teamMemberProfileLinkClicked'
  | 'connectGroupButtonClicked'
  | 'disconnectGroupButtonClicked';

type TrackKeys =
  | 'memberAdded'
  | 'memberRemoved'
  | 'teamNameEdited'
  | 'teamDescriptionEdited'
  | 'pendingMemberAccepted'
  | 'pendingMemberDenied'
  | 'teamDeleted';

export const unifiedTeamProfileEvents: FeatureEvents<ScreenKeys, UiKeys, TrackKeys, never> = {
  confluenceSpec: 'https://hello.atlassian.net/wiki/spaces/PTC/pages/5860493122/Analytics+Spec+Teams+Profile',
  screen: {
    unifiedTeamProfileScreen: ({
      teamId,
      memberCount,
      isVerified,
    }: {
      teamId: string;
      memberCount: number;
      isVerified: boolean;
    }): ScreenEvent => ({
      data: {
        name: unifiedTeamProfileScreen,
        attributes: {
          teamId,
          memberCount,
          isVerified,
        },
      },
      dataPortalLink: '',
    }),
    addTeamModalScreen: ({ teamId }: { teamId: string }): ScreenEvent => ({
      data: {
        name: 'addTeamModalScreen',
        attributes: {
          teamId,
        },
      },
      dataPortalLink: '',
    }),
    editTeamNameModalScreen: ({ teamId, isVerified }: { teamId: string; isVerified: boolean }): ScreenEvent => ({
      data: {
        name: 'editTeamNameModalScreen',
        attributes: {
          teamId,
          isVerified,
        },
      },
      dataPortalLink: '',
    }),
    editTeamDescriptionModalScreen: ({ teamId, isVerified }: { teamId: string; isVerified: boolean }): ScreenEvent => ({
      data: {
        name: 'editTeamDescriptionModalScreen',
        attributes: {
          teamId,
          isVerified,
        },
      },
      dataPortalLink: '',
    }),
    deleteTeamModalScreen: ({ teamId, isVerified }: { teamId: string; isVerified: boolean }): ScreenEvent => ({
      data: {
        name: 'deleteTeamModalScreen',
        attributes: {
          teamId,
          isVerified,
        },
      },
      dataPortalLink: '',
    }),
    removeMemberFromTeamModalScreen: ({ teamId, memberId }: { teamId: string; memberId: string }): ScreenEvent => ({
      data: {
        name: 'removeMemberFromTeamModalScreen',
        attributes: {
          teamId,
          memberId,
        },
      },
      dataPortalLink: '',
    }),
    teamPendingRequestsTabScreen: ({ teamId, pendingRequestCount }: { teamId: string; pendingRequestCount?: number }): ScreenEvent => ({
      data: {
        name: 'teamPendingRequestsTabScreen',
        attributes: {
          teamId,
          pendingRequestCount,
        },
      },
      dataPortalLink: '',
    }),
  },
  ui: {
    addMembersToTeamButtonClicked: ({ teamId }: { teamId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'addTeamMembers',
        source: unifiedTeamProfileScreen,
        attributes: {
          teamId,
        },
      },
      dataPortalLink: '',
    }),
    dialogOpenedTeamDeletion: (attributes: { teamId: string; isVerified: boolean }): UIEvent => ({
      data: {
        action: Action.Opened,
        actionSubject: ActionSubject.Dialog,
        actionSubjectId: 'teamDeletion',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    removeMemberFromTeamButtonClicked: (attributes: { teamId: string; memberId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'removeTeamMember',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    addMembersToTeamModalCancelButtonClicked: (attributes: { teamId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'addMembersToTeamModalCancel',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    dialogClosedTeamDeletion: (attributes: { teamId: string; isVerified: boolean }): UIEvent => ({
      data: {
        action: Action.Closed,
        actionSubject: ActionSubject.Dialog,
        actionSubjectId: 'teamDeletion',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    removeMemberFromTeamModalCancelButtonClicked: (attributes: { teamId: string; memberId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'removeMemberFromTeamModalCancel',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    removeMemberFromTeamConfirmButtonClicked: (attributes: { teamId: string; memberId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'removeMemberFromTeamConfirm',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    addTeamModalConfirmButtonClicked: (attributes: { teamId: string; memberCount: number }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'addTeamModalConfirm',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    deleteTeamConfirmButtonClicked: (attributes: { teamId: string; isVerified: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'deleteTeamConfirm',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    editTeamNameButtonClicked: (attributes: { teamId: string; isVerified: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'editTeamName',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    editTeamDescriptionButtonClicked: (attributes: { teamId: string; isVerified: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'editTeamDescription',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    editTeamNameModalCancelButtonClicked: (attributes: { teamId: string; isVerified: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'editTeamNameModalCancel',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    editTeamDescriptionModalCancelButtonClicked: (attributes: { teamId: string; isVerified: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'editTeamDescriptionModalCancel',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    editTeamNameSubmitButtonClicked: (attributes: { teamId: string; isVerified: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'editTeamNameSubmit',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    editTeamDescriptionSubmitButtonClicked: (attributes: { teamId: string; isVerified: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'editTeamDescriptionSubmit',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    acceptJoinRequestButtonClicked: (attributes: { teamId: string; requesterId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'acceptJoinRequest',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    declineJoinRequestButtonClicked: (attributes: { teamId: string; requesterId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'declineJoinRequest',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    teamMemberProfileLinkClicked: (attributes: { teamId: string; memberId: string; url: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Link,
        actionSubjectId: 'teamMemberProfileLink',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    connectGroupButtonClicked: (attributes: { teamId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'connectGroup',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    disconnectGroupButtonClicked: (attributes: { teamId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'disconnectGroup',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
  },
  track: {
    memberAdded: ({ teamId, members }: { teamId: string; members: number }): TrackEvent => ({
      data: {
        action: TrackAction.Sent,
        actionSubject: TrackActionSubject.TeamInvitation,
        source: unifiedTeamProfileScreen,
        attributes: {
          teamId,
          members,
        },
      },
      dataPortalLink: '',
    }),
    memberRemoved: ({ teamId, memberId, teamSize }: { teamId: string; memberId: string; teamSize: number }): TrackEvent => ({
      data: {
        action: TrackAction.Removed,
        actionSubject: TrackActionSubject.TeamMember,
        source: unifiedTeamProfileScreen,
        attributes: {
          teamId,
          memberId,
          teamSize,
        },
      },
      dataPortalLink: '',
    }),
    teamNameEdited: ({ teamId, isVerified }: { teamId: string; isVerified: boolean }): TrackEvent => ({
      data: {
        action: TrackAction.Edited,
        actionSubject: TrackActionSubject.TeamProfileName,
        source: unifiedTeamProfileScreen,
        attributes: {
          teamId,
          isVerified,
        },
      },
      dataPortalLink: '',
    }),
    teamDescriptionEdited: ({ teamId, isVerified }: { teamId: string; isVerified: boolean }): TrackEvent => ({
      data: {
        action: TrackAction.Edited,
        actionSubject: TrackActionSubject.TeamProfileDescription,
        source: unifiedTeamProfileScreen,
        attributes: {
          teamId,
          isVerified,
        },
      },
      dataPortalLink: '',
    }),
    pendingMemberAccepted: ({ teamId, memberId }: { teamId: string; memberId: string }): TrackEvent => ({
      data: {
        action: TrackAction.Accepted,
        actionSubject: TrackActionSubject.JoinRequest,
        source: unifiedTeamProfileScreen,
        attributes: {
          teamId,
          memberId,
        },
      },
      dataPortalLink: '',
    }),
    pendingMemberDenied: ({ teamId, memberId }: { teamId: string; memberId: string }): TrackEvent => ({
      data: {
        action: TrackAction.Denied,
        actionSubject: TrackActionSubject.JoinRequest,
        source: unifiedTeamProfileScreen,
        attributes: {
          teamId,
          memberId,
        },
      },
      dataPortalLink: '',
    }),
    teamDeleted: ({ teamId, isVerified, memberCount }: { teamId: string; isVerified: boolean; memberCount: number }): TrackEvent => ({
      data: {
        action: TrackAction.Removed,
        actionSubject: TrackActionSubject.Team,
        source: unifiedTeamProfileScreen,
        attributes: {
          teamId,
          isVerified,
          memberCount,
        },
      },
      dataPortalLink: '',
    }),
  },
  operational: {},
};
